/* =========================================================
   Cart Drawer — JavaScript controller
   Depends on global.js: CartAPI, PubSub, debounce, formatMoney
   ========================================================= */

(function () {
  'use strict';

  /* -------------------------------------------------------
     1. Init — wait for DOM
     ------------------------------------------------------- */

  document.addEventListener('DOMContentLoaded', () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    /* --- DOM refs --- */
    const backdrop = drawer.querySelector('[data-cart-drawer-backdrop]');
    const closeBtn = drawer.querySelector('[data-cart-drawer-close]');
    const itemsContainer = drawer.querySelector('[data-cart-drawer-items]');
    const footerEl = drawer.querySelector('[data-cart-drawer-footer]');
    const emptyEl = drawer.querySelector('[data-cart-drawer-empty]');
    const badgeEl = drawer.querySelector('[data-cart-drawer-badge]');
    const htEl = drawer.querySelector('[data-cart-drawer-ht]');
    const tvaEl = drawer.querySelector('[data-cart-drawer-tva]');
    const totalEl = drawer.querySelector('[data-cart-drawer-total]');
    const discountRowEl = drawer.querySelector('[data-cart-drawer-discount-row]');
    const discountEl = drawer.querySelector('[data-cart-drawer-discount]');
    const shippingEl = drawer.querySelector('[data-cart-drawer-shipping]');
    const postalEl = drawer.querySelector('[data-cart-drawer-postal]');
    const postalInput = drawer.querySelector('[data-cart-postal-input]');
    const postalSearchBtn = drawer.querySelector('[data-cart-postal-search]');
    const postalResultEl = drawer.querySelector('[data-cart-postal-result]');

    /* --- Config from Liquid JSON --- */
    const configEl = drawer.querySelector('[data-cart-drawer-config]');
    let config = { routes: {}, translations: {}, references: {} };
    if (configEl) {
      try { config = JSON.parse(configEl.textContent); } catch (e) { /* noop */ }
    }
    const t = config.translations || {};
    /* Tables par variant_id : initialisées par le rendu serveur, puis rafraîchies
       via sections/cart-drawer-data.liquid quand une nouvelle variante entre au panier */
    let refs = config.references || {};
    let comparePrices = config.comparePrices || {};
    let eco = config.ecoParticipation || {};
    var mapsPromise = null;

    function mapsKnowVariant(id) {
      var k = String(id);
      return Object.prototype.hasOwnProperty.call(refs, k)
        || Object.prototype.hasOwnProperty.call(comparePrices, k)
        || Object.prototype.hasOwnProperty.call(eco, k);
    }

    /* Recharge les tables depuis la Section Rendering API (si une variante du panier est inconnue) */
    function ensureMaps(cart) {
      var unknown = cart.items.some(function (it) { return !mapsKnowVariant(it.variant_id); });
      if (!unknown) return Promise.resolve();
      if (mapsPromise) return mapsPromise;
      mapsPromise = fetch('/?sections=cart-drawer-data&_=' + Date.now(), { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (json) {
          var html = json && json['cart-drawer-data'];
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var script = doc.querySelector('script[data-cart-drawer-data]');
          if (!script) return;
          var data = JSON.parse(script.textContent);
          refs = data.references || {};
          comparePrices = data.comparePrices || {};
          eco = data.ecoParticipation || {};
        })
        .catch(function (err) { console.error('[cart-drawer] data refresh failed:', err); })
        .then(function () { mapsPromise = null; });
      return mapsPromise;
    }

    /* Panier à jour + tables à jour (prix barré, badge, référence, éco-participation) */
    function refreshCart() {
      return CartAPI.get().then(function (cart) {
        return ensureMaps(cart).then(function () { return cart; });
      });
    }

    /* Format an eco-participation amount (euros) → "dont X,XX€ d'éco-participation" */
    function formatEco(val) {
      var cents = Math.round((parseFloat(val) || 0) * 100);
      if (!cents) return '';
      return 'dont ' + formatMoney(cents) + ' d\'éco-participation';
    }

    /* -------------------------------------------------------
       2. Open / Close
       ------------------------------------------------------- */

    function open() {
      drawer.classList.add('is-open');
      document.body.classList.add('cart-drawer-open');
      drawer.setAttribute('aria-hidden', 'false');
    }

    function close() {
      drawer.classList.remove('is-open');
      document.body.classList.remove('cart-drawer-open');
      drawer.setAttribute('aria-hidden', 'true');
    }

    /* Close triggers */
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        close();
      }
    });

    /* "Continue shopping" link in empty state */
    const continueLink = drawer.querySelector('[data-cart-drawer-close-link]');
    if (continueLink) {
      continueLink.addEventListener('click', function () {
        close();
      });
    }

    /* -------------------------------------------------------
       3. Helpers
       ------------------------------------------------------- */

    function escapeHtml(str) {
      if (!str) return '';
      var map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
      return String(str).replace(/[&<>"']/g, function (c) { return map[c]; });
    }

    /**
     * Build a sized Shopify image URL.
     * cart.js returns URLs like //cdn.shopify.com/.../file.jpg
     * We insert _WIDTHx before the extension.
     */
    function sizedImageUrl(url, size) {
      if (!url) return '';
      // Remove any existing size suffix (_NNNxNNN)
      var clean = url.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|\d+x\d*|\d*x\d+)(\.[^.]+)$/, '$2');
      // Insert size before extension
      return clean.replace(/(\.[^.]+)$/, '_' + size + '$1');
    }

    /* Starburst SVG for discount badge (reused from cart page) */
    var STARBURST_SVG = '<svg class="cart-drawer__item-badge-shape" width="40" height="40" viewBox="0 0 73 73" fill="none" aria-hidden="true"><path d="M36.4985 3.5032L42.2798 0L46.6943 5.12053L53.2752 3.5726L55.8943 9.80656L62.6292 10.3678L63.1934 17.1056L69.4244 19.7217L67.8795 26.3027L73 30.7171L69.4938 36.4985L73 42.2798L67.8795 46.6943L69.4244 53.2752L63.1934 55.8943L62.6292 62.6292L55.8943 63.1934L53.2752 69.4244L46.6943 67.8795L42.2798 73L36.4985 69.4938L30.7171 73L26.3027 67.8795L19.7217 69.4244L17.1056 63.1934L10.3678 62.6292L9.80656 55.8943L3.5726 53.2752L5.12053 46.6943L0 42.2798L3.5032 36.4985L0 30.7171L5.12053 26.3027L3.5726 19.7217L9.80656 17.1056L10.3678 10.3678L17.1056 9.80656L19.7217 3.5726L26.3027 5.12053L30.7171 0L36.4985 3.5032Z" fill="#F35D5D"/></svg>';

    /* -------------------------------------------------------
       3b. Postal code installer check
       ------------------------------------------------------- */

    var installerLocations = null; /* lazy-loaded */

    function haversine(lat1, lon1, lat2, lon2) {
      var R = 6371;
      var dLat = (lat2 - lat1) * Math.PI / 180;
      var dLon = (lon2 - lon1) * Math.PI / 180;
      var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    function loadInstallerLocations() {
      if (installerLocations) return Promise.resolve(installerLocations);
      var url = (config.routes || {}).installer_locations;
      if (!url) return Promise.resolve([]);
      return fetch(url)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          installerLocations = data.locations || [];
          return installerLocations;
        })
        .catch(function () { return []; });
    }

    function searchInstallers(postal) {
      if (!postalResultEl) return;
      postalResultEl.style.display = '';
      postalResultEl.className = 'cart-drawer__postal-result';
      postalResultEl.textContent = 'Recherche…';
      if (postalSearchBtn) postalSearchBtn.disabled = true;

      var RADIUS = 50; /* km */
      var installerPageUrl = (config.routes || {}).installer_page || '/pages/trouver-un-installateur';

      fetch('https://api-adresse.data.gouv.fr/search/?q=' + encodeURIComponent(postal) + '&type=municipality&limit=1')
        .then(function (r) { return r.json(); })
        .then(function (geoData) {
          if (!geoData.features || geoData.features.length === 0) {
            postalResultEl.className = 'cart-drawer__postal-result cart-drawer__postal-result--error';
            postalResultEl.textContent = 'Code postal non reconnu.';
            if (postalSearchBtn) postalSearchBtn.disabled = false;
            return;
          }

          var coords = geoData.features[0].geometry.coordinates;
          var lat = coords[1], lng = coords[0];

          return loadInstallerLocations().then(function (locations) {
            var nearby = [];
            locations.forEach(function (loc) {
              var dist = haversine(lat, lng, loc.lat, loc.lng);
              if (dist <= RADIUS) nearby.push({ loc: loc, dist: dist });
            });

            if (nearby.length > 0) {
              postalResultEl.className = 'cart-drawer__postal-result cart-drawer__postal-result--success';
              postalResultEl.innerHTML = '<strong>' + nearby.length + ' installateur' + (nearby.length > 1 ? 's' : '') + '</strong> référencé' + (nearby.length > 1 ? 's' : '') + ' à proximité. <a href="' + escapeHtml(installerPageUrl) + '">Voir la carte</a>';
            } else {
              postalResultEl.className = 'cart-drawer__postal-result cart-drawer__postal-result--error';
              postalResultEl.innerHTML = 'Aucun installateur dans un rayon de ' + RADIUS + ' km. <a href="' + escapeHtml(installerPageUrl) + '">Voir tous les installateurs</a>';
            }
            if (postalSearchBtn) postalSearchBtn.disabled = false;
          });
        })
        .catch(function () {
          postalResultEl.className = 'cart-drawer__postal-result cart-drawer__postal-result--error';
          postalResultEl.textContent = 'Erreur de recherche. Réessayez.';
          if (postalSearchBtn) postalSearchBtn.disabled = false;
        });
    }

    /* Wire postal code search */
    if (postalSearchBtn && postalInput) {
      postalSearchBtn.addEventListener('click', function () {
        var postal = postalInput.value.trim();
        if (/^\d{5}$/.test(postal)) {
          searchInstallers(postal);
        }
      });
      postalInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          var postal = postalInput.value.trim();
          if (/^\d{5}$/.test(postal)) {
            searchInstallers(postal);
          }
        }
      });
    }

    /* -------------------------------------------------------
       4. Render drawer from cart JSON
       ------------------------------------------------------- */

    function renderDrawer(cart) {
      /* Update badges (drawer + header) */
      if (badgeEl) {
        badgeEl.textContent = cart.item_count;
        badgeEl.style.display = cart.item_count > 0 ? '' : 'none';
      }
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
      });

      /* Empty state toggle */
      if (cart.item_count === 0) {
        if (itemsContainer) { itemsContainer.innerHTML = ''; itemsContainer.style.display = 'none'; }
        if (footerEl) footerEl.style.display = 'none';
        if (shippingEl) shippingEl.style.display = 'none';
        if (postalEl) postalEl.style.display = 'none';
        if (emptyEl) emptyEl.style.display = '';
        return;
      }

      if (itemsContainer) itemsContainer.style.display = '';
      if (footerEl) footerEl.style.display = '';
      if (shippingEl) shippingEl.style.display = '';
      if (emptyEl) emptyEl.style.display = 'none';

      /* Build items HTML */
      var html = '';
      var totalDiscount = 0;

      cart.items.forEach(function (item, index) {
        var line = index + 1;
        var comparePrice = comparePrices[String(item.variant_id)] || 0;
        var onSale = comparePrice > item.final_price;
        var savingsPct = onSale
          ? Math.round((comparePrice - item.final_price) / comparePrice * 100)
          : 0;

        if (onSale) {
          totalDiscount += (comparePrice - item.final_price) * item.quantity;
        }

        /* Reference from config map */
        var ref = refs[String(item.variant_id)] || '';

        var imgUrl = item.image ? sizedImageUrl(item.image, '300x') : '';

        html += '<div class="cart-drawer__item" data-cart-drawer-item data-line="' + line + '">';

        /* Image + badge */
        html += '<div class="cart-drawer__item-media">';
        html += '<a href="' + escapeHtml(item.url) + '" aria-label="' + escapeHtml(item.product_title) + '">';
        if (imgUrl) {
          html += '<img src="' + escapeHtml(imgUrl) + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy" width="130" height="130">';
        }
        html += '</a>';
        if (onSale && String(item.vendor || '').toLowerCase() !== 'saunier duval') {
          html += '<div class="cart-drawer__item-badge">' + STARBURST_SVG;
          html += '<span class="cart-drawer__item-badge-text">-' + savingsPct + '%</span></div>';
        }
        html += '</div>';

        /* Details */
        html += '<div class="cart-drawer__item-details">';
        html += '<a href="' + escapeHtml(item.url) + '" class="cart-drawer__item-title">' + escapeHtml(item.product_title) + '</a>';

        /* Meta: type · vendor */
        html += '<div class="cart-drawer__item-meta">';
        if (item.product_type) {
          html += '<span>' + escapeHtml(item.product_type) + '</span>';
          html += '<span class="cart-drawer__meta-dot" aria-hidden="true"></span>';
        }
        html += '<span>' + escapeHtml(item.vendor) + '</span>';
        html += '</div>';

        /* Reference */
        if (ref) {
          html += '<p class="cart-drawer__item-ref">' + escapeHtml(t.reference || 'REF') + '  ' + escapeHtml(ref) + '</p>';
        }

        /* Pricing — TTC + compare on row 1, HT below, eco below */
        var taxLabel = getTaxLabel();
        var unitHt = Math.round(item.final_price * 100 / 120);
        var ecoStr = formatEco(eco[String(item.variant_id)]);
        html += '<div class="cart-drawer__item-pricing">';
        html += '<div class="cart-drawer__item-price-row">';
        html += '<span class="cart-drawer__item-price" data-price-cents="' + item.final_price + '">' + formatMoney(getDisplayPrice(item.final_price)) + ' ' + escapeHtml(taxLabel) + '</span>';
        if (onSale) {
          html += '<s class="cart-drawer__item-compare" data-price-cents="' + comparePrice + '">' + formatMoney(getDisplayPrice(comparePrice)) + ' ' + escapeHtml(taxLabel) + '</s>';
        }
        html += '</div>';
        html += '<span class="cart-drawer__item-ht">' + formatMoney(unitHt) + ' HT</span>';
        if (ecoStr) {
          html += '<span class="cart-drawer__item-eco">' + escapeHtml(ecoStr) + '</span>';
        }
        html += '</div>';

        /* Quantity selector */
        html += '<div class="cart-drawer__qty">';
        html += '<button type="button" class="cart-drawer__qty-btn" data-cart-drawer-minus aria-label="Diminuer">';
        html += '<svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true"><circle cx="11" cy="11" r="10.5" stroke="currentColor" stroke-opacity="0.6"/><path d="M7 11h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
        html += '</button>';
        html += '<input type="number" class="cart-drawer__qty-input" data-cart-drawer-qty-input value="' + item.quantity + '" min="1" aria-label="Quantité">';
        html += '<button type="button" class="cart-drawer__qty-btn" data-cart-drawer-plus aria-label="Augmenter">';
        html += '<svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true"><circle cx="11" cy="11" r="10.5" stroke="currentColor" stroke-opacity="0.6"/><path d="M11 7v8M7 11h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
        html += '</button>';
        html += '</div>';

        html += '</div>'; /* end details */

        /* Remove button */
        html += '<button type="button" class="cart-drawer__item-remove" data-cart-drawer-remove aria-label="' + escapeHtml(t.remove || 'Retirer') + '">';
        html += '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
        html += '</button>';

        html += '</div>'; /* end item */
      });

      if (itemsContainer) itemsContainer.innerHTML = html;

      /* Update footer totals: HT, TVA (20%), Réduction, TTC */
      var ttc = cart.total_price;
      var ht = Math.round(ttc * 100 / 120);
      var tva = ttc - ht;

      if (htEl) htEl.textContent = formatMoney(ht);
      if (tvaEl) tvaEl.textContent = formatMoney(tva);
      if (totalEl) totalEl.textContent = formatMoney(getDisplayPrice(ttc)) + ' ' + getTaxLabel();

      if (discountRowEl && discountEl) {
        if (totalDiscount > 0) {
          discountEl.textContent = '-' + formatMoney(getDisplayPrice(totalDiscount));
          discountRowEl.style.display = '';
        } else {
          discountRowEl.style.display = 'none';
        }
      }

      /* Update shipping status — note when paid (calculée au checkout), "offerte" when free */
      if (shippingEl) {
        var FREE_THRESHOLD = (config.freeShippingThreshold || 120000);
        var truckSvg = '<svg width="16" height="12" viewBox="0 0 16 12" fill="none" aria-hidden="true"><path d="M1 2h9v7H1V2z" stroke="currentColor" stroke-width="1"/><path d="M10 4h3l2 2v3h-5V4z" stroke="currentColor" stroke-width="1"/><circle cx="4" cy="9" r="1.2" stroke="currentColor" stroke-width="1"/><circle cx="13" cy="9" r="1.2" stroke="currentColor" stroke-width="1"/></svg>';

        var freeShip = ttc >= FREE_THRESHOLD;
        cart.items.forEach(function (it) { if (it.final_price >= FREE_THRESHOLD) freeShip = true; });

        if (freeShip) {
          shippingEl.innerHTML = '<div class="cart-drawer__shipping-free">' + truckSvg + '<span>Livraison offerte</span></div>';
        } else {
          var remaining = FREE_THRESHOLD - ttc;
          var pct = Math.min(99, Math.floor(ttc * 100 / FREE_THRESHOLD));
          shippingEl.innerHTML = '<div class="cart-drawer__shipping-estimate">' + truckSvg
            + '<span>Livraison calcul\u00e9e \u00e0 l\'\u00e9tape suivante</span></div>'
            + '<div class="cart-drawer__shipping-progress"><span>Plus que <strong>' + formatMoney(remaining) + '</strong> pour la livraison offerte</span>'
            + '<div class="cart-drawer__shipping-bar"><div class="cart-drawer__shipping-bar-fill" style="width: ' + pct + '%"></div></div></div>';
        }
      }

      /* Toggle postal code section (visible only if Saunier Duval in cart) */
      if (postalEl) {
        var hasSaunier = false;
        cart.items.forEach(function (item) {
          if (item.vendor && item.vendor.toLowerCase() === 'saunier duval') hasSaunier = true;
        });
        postalEl.style.display = hasSaunier ? '' : 'none';
      }
    }

    /* -------------------------------------------------------
       5. Quantity / Remove — event delegation
       ------------------------------------------------------- */

    var handleQtyChange = debounce(function (line, qty) {
      var item = itemsContainer
        ? itemsContainer.querySelector('[data-cart-drawer-item][data-line="' + line + '"]')
        : null;

      if (item) {
        item.style.opacity = '0.5';
        item.style.pointerEvents = 'none';
      }

      CartAPI.change(line, qty)
        .then(function (cart) {
          renderDrawer(cart);
        })
        .catch(function (err) {
          console.error('[cart-drawer] qty change failed:', err);
          if (item) {
            item.style.opacity = '';
            item.style.pointerEvents = '';
          }
        });
    }, 400);

    if (itemsContainer) {
      /* Saisie clavier de la quantité (Entrée ou perte de focus déclenchent 'change') */
      itemsContainer.addEventListener('change', function (e) {
        var qtyInput = e.target.closest('[data-cart-drawer-qty-input]');
        if (!qtyInput) return;
        var rowEl = qtyInput.closest('[data-cart-drawer-item]');
        if (!rowEl) return;
        var rowLine = parseInt(rowEl.getAttribute('data-line'), 10);
        var rowQty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
        qtyInput.value = rowQty;
        handleQtyChange(rowLine, rowQty);
      });

      itemsContainer.addEventListener('click', function (e) {
        var minusBtn = e.target.closest('[data-cart-drawer-minus]');
        var plusBtn = e.target.closest('[data-cart-drawer-plus]');
        var removeBtn = e.target.closest('[data-cart-drawer-remove]');

        if (minusBtn || plusBtn) {
          e.preventDefault();
          var itemEl = e.target.closest('[data-cart-drawer-item]');
          if (!itemEl) return;
          var line = parseInt(itemEl.getAttribute('data-line'), 10);
          var input = itemEl.querySelector('[data-cart-drawer-qty-input]');
          var qty = parseInt(input.value, 10) || 1;
          if (minusBtn) qty = Math.max(1, qty - 1);
          if (plusBtn) qty += 1;
          input.value = qty;
          handleQtyChange(line, qty);
        }

        if (removeBtn) {
          e.preventDefault();
          var itemEl2 = removeBtn.closest('[data-cart-drawer-item]');
          if (!itemEl2) return;
          var line2 = parseInt(itemEl2.getAttribute('data-line'), 10);
          handleQtyChange(line2, 0);
        }
      });
    }

    /* -------------------------------------------------------
       6. PubSub subscriptions
       ------------------------------------------------------- */

    /* When cart data updates from any source, refresh the drawer contents */
    PubSub.subscribe('cart-update', function () {
      /* cart-update from add() returns added items only, not full cart.
         Always fetch fresh full cart for re-rendering. */
      refreshCart().then(function (cart) {
        renderDrawer(cart);
      });
    });

    /* When tax mode changes, re-render with current cart data */
    PubSub.subscribe('tax-mode-change', function () {
      CartAPI.get().then(function (cart) {
        renderDrawer(cart);
      });
    });

    /* Open the drawer (triggered by product form after add-to-cart) */
    PubSub.subscribe('cart-drawer:open', function () {
      refreshCart().then(function (cart) {
        renderDrawer(cart);
        open();
      });
    });

    /* -------------------------------------------------------
       7. Wire header cart icon toggle
       ------------------------------------------------------- */

    var cartToggle = document.querySelector('[data-cart-drawer-toggle]');
    if (cartToggle) {
      cartToggle.addEventListener('click', function (e) {
        e.preventDefault();
        refreshCart().then(function (cart) {
          renderDrawer(cart);
          open();
        });
      });
    }
  });
})();
